/**
 * @file PageSwitcher.tsx
 * @description Переключатель страниц конструктора (phase19.16 Р1-6а;
 * uiux-builder-shell.md §2.1): открытая страница (название, адрес, статус);
 * в списке — страницы, поиск при > 8, группа «Скрытые · N», «+ Страница»,
 * раздел «Экраны магазина — меняется только вид» (каталог, товар, корзина,
 * оформление заказа, кабинет, блог). Р2а: «Каталог» — вид списка и блоки
 * над/под списком, «Страница товара» — шаблон из блоков.
 * Р3а (uiux-r3 §3.1): у страниц статус «на сайте / правки / скрыта / ещё не
 * была на сайте» и точка «есть правки»; внизу — «Удалённые страницы».
 */

import { SITE_SLOTS } from "@chaiberi/shared";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { SearchInput } from "../../ui/SearchInput";
import { useBuilder } from "../builder-context";
import { PageSwitcherRow as Row } from "./PageSwitcherRow";
import type { SitePageListItem } from "../../../lib/api-site-pages";
import { BUILDER_SCREENS, type BuilderScreenKey } from "../../../lib/site-builder/selection";
import { BUILDER_SCREEN_TITLES } from "../../../lib/site-builder/panels";
import { PREVIEW_SCREENS } from "../../../lib/site-view/preview";
import { PAGE_STATUS_META, pageHasDot, pageStatusOf, withFreshChanges, type PageStatusInput } from "../../../lib/site-builder/page-status";

/** Адрес для строки; у места служебного адреса `/_slot-…` не показываем — его название (QA Р3а D11). */
const pagePath = (p: { kind: string; slug: string; slot?: string | null }) =>
  p.kind === "SLOT" ? (SITE_SLOTS.find((s) => s.key === p.slot)?.label ?? "место для блоков") : p.kind === "SYSTEM_HOME" ? "/" : `/${p.slug}`;
const screenLabel = (key: BuilderScreenKey) => BUILDER_SCREEN_TITLES[key] ?? PREVIEW_SCREENS.find((s) => s.key === key)?.label ?? key;

/** Что меняется на экране (строка под названием). */
const SCREEN_META: Readonly<Partial<Record<BuilderScreenKey, string>>> = {
  catalog: "Вид списка и карточки, блоки над и под списком",
  product: "Шаблон из блоков — для всех товаров",
  blog: "Вид ленты и статьи, блоки над и под лентой",
  cart: "Окно из шапки или панель сбоку, цвета и размеры",
};

export function PageSwitcher() {
  const b = useBuilder();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const list = b.pages.list ?? [];
  const current = b.pages.page;
  const screen = b.selection.screen;
  const q = query.trim().toLowerCase();
  const found = q ? list.filter((p) => p.title.toLowerCase().includes(q) || p.slug.includes(q)) : list;
  const shown = found.filter((p) => p.isPublished);
  const hidden = found.filter((p) => !p.isPublished);

  const go = (patch: { pageId: string | null; screen: BuilderScreenKey | null }) => {
    setOpen(false);
    void b.select({ ...patch, panel: null });
  };
  // Открытая страница — по свежей сводке черновика, остальные — по списку.
  const fresh = b.publishing.changes;
  const withFresh = <T extends PageStatusInput & { id: string }>(p: T): T => withFreshChanges(p, fresh);
  const pageRow = (raw: SitePageListItem) => {
    const p = withFresh(raw);
    const status = pageStatusOf(p);
    return (
      <Row
        key={p.id}
        title={p.title}
        meta={`${pagePath(p)} · ${PAGE_STATUS_META[status].label}`}
        status={status}
        dot={status !== "changes" && pageHasDot(p)}
        active={!screen && current?.id === p.id}
        onClick={() => go({ pageId: p.id, screen: null })}
      />
    );
  };

  const triggerTitle = screen ? screenLabel(screen) : (current?.title ?? "Главная");
  const triggerMeta = screen ? (SCREEN_META[screen] ?? "Экран магазина · меняется только вид") : current
      ? `${pagePath(current)} · ${PAGE_STATUS_META[pageStatusOf(withFresh(current))].label}`
      : b.canEditPages
        ? "Загружаем…"
        : "Страницы — у сотрудника с правом на контент";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={(props) => (
          <button
            {...props}
            type="button"
            className="flex min-h-14 w-full items-center gap-2 rounded-xl bg-surface-lowest px-3 py-2 text-left outline-none [box-shadow:inset_0_0_0_1px_var(--border)] focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="type-body truncate font-medium text-on-surface">{triggerTitle}</span>
              <span className="type-caption truncate text-on-surface-variant">{triggerMeta}</span>
            </span>
            <ChevronDown aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
          </button>
        )}
      />
      <PopoverContent align="start" className="flex max-h-[70dvh] w-80 max-w-[calc(100vw-2rem)] flex-col gap-1 overflow-y-auto p-2">
        {list.length > 8 ? <SearchInput value={query} onChange={setQuery} placeholder="Найти страницу" ariaLabel="Поиск страницы" /> : null}
        {b.pages.listError ? <p className="type-caption p-2 text-on-surface-variant">{b.pages.listError}</p> : null}
        {shown.map(pageRow)}
        {hidden.length > 0 ? <span className="type-caption px-3 pt-2 text-on-surface-variant">Скрытые · {hidden.length}</span> : null}
        {hidden.map(pageRow)}
        {b.canEditPages ? (
          <Button
            variant="ghost"
            className="justify-start"
            onClick={() => {
              setOpen(false);
              b.openAddPage();
            }}
          >
            <Plus aria-hidden />
            Страница
          </Button>
        ) : null}
        {b.canEditPages ? (
          <Button
            variant="ghost"
            className="justify-start text-on-surface-variant"
            onClick={() => {
              setOpen(false);
              b.openDeletedPages();
            }}
          >
            <Trash2 aria-hidden />
            Удалённые страницы
          </Button>
        ) : null}
        <span className="type-caption px-3 pt-2 text-on-surface-variant">Экраны магазина — меняется только вид</span>
        {BUILDER_SCREENS.map((key) => (
          <Row key={key} title={screenLabel(key)} meta={SCREEN_META[key] ?? "Шапка, подвал и оформление"} active={screen === key} onClick={() => go({ pageId: null, screen: key })} />
        ))}
      </PopoverContent>
    </Popover>
  );
}
